/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.currentTotalItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0
      );

      this.currentQuantities = [];
      this.querySelectorAll('.cart-item').forEach((item) => {
        this.currentQuantities[item.dataset.variantId] = Number(item.querySelector('.qty-input__input').value);
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));

      this.cartRefreshHandler = this.refreshCart.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);

      setTimeout(() => {
        this.updateAdjustedComparePrices();
      }, 150);
    }

    handleClick(evt) {
      if (!evt.target.matches('.js-remove-item')) return;
      evt.preventDefault();
      this.updateQuantity(evt.target.dataset.index, 0);
    }

    handleChange(evt) {
      this.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name);
    }

    async updateQuantity(line, quantity, name) {
      this.enableLoading(line);

      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);

      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      try {
        const lineItem = document.getElementById(`cart-item-${line}`);
        const variantId = lineItem ? Number(lineItem.dataset.variantId) : null;
        const oldTotalQuantity = this.currentTotalItemCount;

        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const newTotalQuantity = data.item_count;

        if (this.cartDrawer) {
          const drawerContent = this.cartDrawer.querySelector('.drawer__content');
          if (drawerContent) {
            drawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);
          }

          if (newTotalQuantity === 0) {
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();
          }
        } else if (newTotalQuantity === 0) {
          const cartTitle = this.closest('.cc-main-cart')?.querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartSummary = document.getElementById('cart-summary');
          if (cartSummary) cartSummary.hidden = true;
        }

        // Drawer: let cart-drawer handle the full section rerender
        if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
          await this.cartDrawer.renderContents(data, true);
        } else {
          // Cart page: keep partial section replacement
          this.getSectionsToRender().forEach((section) => {
            const sectionEl = document.getElementById(section.id);
            if (!sectionEl) return;

            const { selector } = section;
            const el = sectionEl.querySelector(selector) || sectionEl;
            el.innerHTML = CartItems.getElementHTML(data.sections[section.section], selector);
          });
        }

        setTimeout(() => {
          const currentCartItems = document.getElementById('cart-items');
          if (currentCartItems && typeof currentCartItems.updateAdjustedComparePrices === 'function') {
            currentCartItems.updateAdjustedComparePrices();
          }
        }, 150);

        this.updateRecommendations(data.item_count > 0 && data.items[0] ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        if (variantId !== null && oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: data,
              variantId,
              oldQuantity: this.currentQuantities[variantId],
              newQuantity: Number(quantity)
            }
          }));
        }

        if (variantId !== null) {
          this.currentQuantities[variantId] = Number(quantity);
        }

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        this.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        const input = document.getElementById(`quantity-${line}`);
        if (input) {
          input.value = input.dataset.initialValue;
          const quantityInput = input.closest('quantity-input');
          if (quantityInput) quantityInput.currentQty = input.dataset.initialValue;
        }
      } finally {
        this.classList.remove('pointer-events-none');
      }
    }

    async refreshCart() {
      const errors = document.getElementById('cart-errors');

      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${sections.join(',')}`);
        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
          const drawerSectionId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');

          await this.cartDrawer.renderContents({
            sections: {
              [drawerSectionId]: data[drawerSectionId],
              'cart-icon-bubble': data['cart-icon-bubble']
            }
          }, false);
        } else {
          this.getSectionsToRender().forEach((section) => {
            const sectionEl = document.getElementById(section.id);
            if (!sectionEl) return;

            const el = sectionEl.querySelector(section.selector) || sectionEl;
            el.innerHTML = CartItems.getElementHTML(data[section.section], section.selector);
          });
        }

        setTimeout(() => {
          const currentCartItems = document.getElementById('cart-items');
          if (currentCartItems && typeof currentCartItems.updateAdjustedComparePrices === 'function') {
            currentCartItems.updateAdjustedComparePrices();
          }
        }, 150);

        const firstCartItem = document.querySelector('#cart-items .cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        errors.innerHTML = '';
        errors.hidden = true;
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
      }
    }

    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.drawer__footer'
          }
        ];
      } else {
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          {
            id: 'cart-summary',
            section: document.getElementById('cart-summary').dataset.section,
            selector: '.cart__summary'
          }
        ];
      }

      return sections;
    }

    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    parseMoneyToCents(text) {
      if (!text) return 0;

      const normalized = text
        .replace(/[^\d.,-]/g, '')
        .replace(/,/g, '');

      const value = parseFloat(normalized);
      return Number.isNaN(value) ? 0 : Math.round(value * 100);
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, theme.money_format || window.Shopify.money_format);
      }

      return (cents / 100).toFixed(2);
    }

    updateAdjustedComparePrices() {
      this.querySelectorAll('.ymq-adjusted-compare-price').forEach((strikeEl) => {
        const key = strikeEl.dataset.itemKey;
        const currentEl = this.querySelector(`.ymq-current-price[data-item-key="${key}"]`);
        if (!currentEl) return;

        const basePrice = Number(currentEl.dataset.basePrice || 0);
        const baseCompareAt = Number(currentEl.dataset.baseCompareAtPrice || 0);

        if (!(baseCompareAt > basePrice)) {
          strikeEl.hidden = true;
          return;
        }

        const currentTextEl =
          currentEl.querySelector('.ymq_item_final_price, .ymq_item_original_price') || currentEl;
        const currentCents = this.parseMoneyToCents(currentTextEl.textContent);

        const adjustedCompareCents = currentCents + (baseCompareAt - basePrice);

        if (adjustedCompareCents > currentCents) {
          const textEl = strikeEl.querySelector('.ymq-adjusted-compare-price__text');
          if (textEl) textEl.textContent = this.formatMoney(adjustedCompareCents);
          strikeEl.hidden = false;
        } else {
          strikeEl.hidden = true;
        }
      });

      this.querySelectorAll('.ymq-adjusted-compare-line-price').forEach((strikeEl) => {
        const key = strikeEl.dataset.itemKey;
        const currentEl = this.querySelector(`.ymq-current-line-price[data-item-key="${key}"]`);
        if (!currentEl) return;

        const basePrice = Number(currentEl.dataset.basePrice || 0);
        const baseCompareAt = Number(currentEl.dataset.baseCompareAtPrice || 0);
        const qty = Number(currentEl.dataset.qty || 1);

        if (!(baseCompareAt > basePrice)) {
          strikeEl.hidden = true;
          return;
        }

        const currentTextEl =
          currentEl.querySelector('.ymq_item_final_line_price, .ymq_item_original_line_price') || currentEl;
        const currentLineCents = this.parseMoneyToCents(currentTextEl.textContent);

        const adjustedCompareLineCents = currentLineCents + ((baseCompareAt - basePrice) * qty);

        if (adjustedCompareLineCents > currentLineCents) {
          const textEl = strikeEl.querySelector('.ymq-adjusted-compare-line-price__text');
          if (textEl) textEl.textContent = this.formatMoney(adjustedCompareLineCents);
          strikeEl.hidden = false;
        } else {
          strikeEl.hidden = true;
        }
      });
    }

    enableLoading(line) {
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    updateLiveRegions() {
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      if (!cartStatus) return;

      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      const drawer = document.getElementById('cart-drawer');

      if (drawer) {
        if (lineItem && activeEl) {
          trapFocus(drawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(
            drawer.querySelector('.js-cart-empty'),
            drawer.querySelector('a')
          );
        } else if (drawer.querySelector('.cart-item')) {
          trapFocus(drawer, drawer.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}